import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import {
  CustomRewardExperience,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useAutoscroll } from '@sorare/core/src/hooks/useAutoscroll';
import { so5RewardConfigsToSimpleRewards } from '@sorare/core/src/lib/rewards';

import { RewardOverview } from '@sorare/gameplay/src/components/rewards/RewardOverview';
import { Variant } from '@sorare/gameplay/src/components/rewards/RewardOverview/types';

import {
  DiamondWrapper,
  LeaguePodiumIcon,
} from 'components/rivalsUserGroup/LeaguePodiumIcon';
import { LeagueRewardItemDescription } from 'components/rivalsUserGroup/LeagueRewardItemDescription';
import { LeagueRewardItemTitle } from 'components/rivalsUserGroup/LeagueRewardItemTitle';
import { ZzReward } from 'components/rivalsUserGroup/ZzReward';

import { LeagueRewardItem_so5RewardConfig } from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  background: var(--c-neutral-200);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  scroll-margin-top: calc(var(--double-unit) + var(--current-stack-height));
`;

const RewardRowWrapper = styled(Horizontal)`
  padding: var(--double-unit) 0;
  border-bottom: 1px solid var(--c-neutral-300);

  &:last-of-type {
    border: none;
    padding-bottom: 0;
  }

  & > *:first-child {
    flex: 1;
  }
`;
const RewardWrapper = styled.div`
  border: 1px solid var(--c-neutral-300);
  border-radius: var(--double-unit);
  overflow: hidden;
  & > * {
    width: 60px;
    min-height: 60px;
  }
`;

type Props = {
  so5RewardConfig: Nullable<LeagueRewardItem_so5RewardConfig>;
  selected?: boolean;
};
export const LeagueRewardItem = ({ so5RewardConfig, selected }: Props) => {
  const { exchangeRate } = useConfigContext();
  const rewards =
    so5RewardConfig &&
    so5RewardConfigsToSimpleRewards({
      so5RewardConfigs: [so5RewardConfig],
      rarity: Rarity.limited,
      rates: exchangeRate.ethRates,
    });

  const { ref } = useAutoscroll<HTMLDivElement>(selected ?? false);

  if (!rewards?.length) {
    return null;
  }
  const { fromRank, toRank } = so5RewardConfig || {};
  return (
    <Wrapper ref={ref}>
      {fromRank === toRank ? (
        <LeaguePodiumIcon rank={fromRank} size={70} />
      ) : (
        <DiamondWrapper text={`${fromRank}-${toRank}`} size={70} />
      )}
      {rewards?.map((reward, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <RewardRowWrapper key={index}>
          <Vertical gap={0}>
            <LabelL bold>
              <LeagueRewardItemTitle reward={reward} />
            </LabelL>
            <LabelM color="var(--c-neutral-600)">
              <LeagueRewardItemDescription reward={reward} />
            </LabelM>
          </Vertical>
          <RewardWrapper>
            {reward.type === CustomRewardExperience.EVENT &&
            reward.title?.includes('Zidane') ? (
              <ZzReward withTitle />
            ) : (
              <RewardOverview simpleReward={reward} variant={Variant.CLASSIC} />
            )}
          </RewardWrapper>
        </RewardRowWrapper>
      ))}
    </Wrapper>
  );
};

LeagueRewardItem.fragments = {
  so5RewardConfig: gql`
    fragment LeagueRewardItem_so5RewardConfig on So5RewardConfig {
      fromRank
      toRank
      experiences {
        description
      }
      ...so5RewardConfigsToSimpleRewards_so5RewardConfig
    }
    ${so5RewardConfigsToSimpleRewards.fragments.so5RewardConfig}
  ` as TypedDocumentNode<LeagueRewardItem_so5RewardConfig>,
};
